import React from "react"

import globalStyles from "../../../../App.module.scss";
import {Arrow, Box, Diagram, DiagramCaption} from "../../Architecture/shared/Diagram"

export default function DiskPartitioningLvm() {
    return (
        <div className={globalStyles.article}>
            <h1>Disk partitioning &amp; LVM basics</h1>

            <h3>Partitioning a raw disk</h3>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> lsblk <i># see attached disks and their partitions</i><br/>
    <b>root@host:~#</b> parted /dev/sdb --script mklabel gpt mkpart primary 0% 100%<br/>
    <b>root@host:~#</b> mkfs.ext4 /dev/sdb1<br/>
    <b>root@host:~#</b> mount /dev/sdb1 /mnt/data<br/>
            </pre>
            <p>That's fine for a single, static disk. LVM adds a layer that lets you resize, add and combine
                disks without unmounting or re-partitioning.</p>

            <h3>LVM layers</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '330px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Concepts</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>PV</b> — Physical Volume</td>
                        <td>A raw disk or partition, initialized for LVM use</td>
                    </tr>
                    <tr>
                        <td><b>VG</b> — Volume Group</td>
                        <td>A pool made of one or more PVs — the combined storage capacity</td>
                    </tr>
                    <tr>
                        <td><b>LV</b> — Logical Volume</td>
                        <td>A "virtual partition" carved out of a VG — this is what you format and mount</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <Diagram viewBox="0 0 720 370">
                <rect x={370} y={10} width={320} height={330} rx={10} fill="none"
                      stroke="var(--header-background)" strokeDasharray="4 4" opacity={0.4}/>
                <text x={530} y={25} textAnchor="middle" fontSize={12} opacity={0.8}>Admin commands</text>

                <Box x={40} y={20} width={300} height={40} label="Filesystem"
                     sublabel="ext4 / xfs — mounted at /mnt/app" accent/>
                <Box x={380} y={20} width={300} height={40} label="mkfs · resize2fs"
                     sublabel="xfs_growfs · df -h"/>

                <Box x={40} y={90} width={300} height={40} label="Logical Volume (LV)"
                     sublabel="data-vg/app-lv"/>
                <Box x={380} y={90} width={300} height={40} label="lvcreate · lvextend"
                     sublabel="lvs · lvdisplay"/>

                <Box x={40} y={160} width={300} height={40} label="Volume Group (VG)"
                     sublabel="data-vg — pooled capacity"/>
                <Box x={380} y={160} width={300} height={40} label="vgcreate · vgextend"
                     sublabel="vgs · vgdisplay"/>

                <Box x={40} y={230} width={300} height={40} label="Physical Volume (PV)"
                     sublabel="/dev/sdb1 + /dev/sdc1"/>
                <Box x={380} y={230} width={300} height={40} label="pvcreate · pvresize"
                     sublabel="pvs · pvdisplay"/>

                <Box x={40} y={300} width={140} height={40} label="sdb1" sublabel="raw disk/partition"/>
                <Box x={200} y={300} width={140} height={40} label="sdc1" sublabel="raw disk/partition"/>
                <Box x={380} y={300} width={300} height={40} label="parted · fdisk" sublabel="lsblk"/>

                <Arrow x1={110} y1={300} x2={190} y2={270}/>
                <Arrow x1={270} y1={300} x2={190} y2={270}/>
                <Arrow x1={190} y1={230} x2={190} y2={200}/>
                <Arrow x1={190} y1={160} x2={190} y2={130}/>
                <Arrow x1={190} y1={90} x2={190} y2={60}/>
            </Diagram>
            <DiagramCaption>Disks become PVs, PVs pool into a VG, a VG is carved into LVs, and an LV is
                formatted with a filesystem — the layer applications and users actually see.</DiagramCaption>

            <h3>Building it up</h3>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> pvcreate /dev/sdb1 /dev/sdc1<br/>
    <b>root@host:~#</b> vgcreate data-vg /dev/sdb1 /dev/sdc1<br/>
    <b>root@host:~#</b> lvcreate -L 100G -n app-lv data-vg<br/>
    <b>root@host:~#</b> mkfs.ext4 /dev/data-vg/app-lv<br/>
    <b>root@host:~#</b> mount /dev/data-vg/app-lv /mnt/app<br/>
            </pre>

            <h3>Growing a volume online</h3>
            <p>Add a new disk to the group, then extend the logical volume and its filesystem — no downtime, no
                unmount needed for ext4/xfs online resize.</p>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> pvcreate /dev/sdd1<br/>
    <b>root@host:~#</b> vgextend data-vg /dev/sdd1<br/>
    <b>root@host:~#</b> lvextend -L +50G /dev/data-vg/app-lv<br/>
    <b>root@host:~#</b> resize2fs /dev/data-vg/app-lv <i># ext4; use "xfs_growfs /mnt/app" for xfs</i><br/>
            </pre>

            <h3>Growing after resizing the virtual disk (e.g. in Proxmox)</h3>
            <p>If you grew the underlying virtual disk from the hypervisor instead of adding a new one, the
                guest won't see the new size until you rescan the SCSI bus. Then grow the partition, the PV
                and the LV in that order — all online, no reboot or unmount needed.</p>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> lsblk <i># disk still shows the old size</i><br/>
    <b>root@host:~#</b> for d in /sys/class/scsi_device/*/device/rescan; do echo 1 &gt; "$d"; done<br/>
    <b>root@host:~#</b> lsblk <i># disk is now bigger, but the partition (e.g. sda3) is still the old size</i><br/>
            </pre>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> growpart /dev/sda 3 <i># cloud-guest-utils; grows partition 3 in place, no data loss</i><br/>
    <b>root@host:~#</b> pvresize /dev/sda3 <i># extend the PV to the new partition size</i><br/>
    <b>root@host:~#</b> lvextend -l +100%FREE /dev/data-vg/app-lv <i># grow the LV to use all the new space</i><br/>
    <b>root@host:~#</b> resize2fs /dev/data-vg/app-lv <i># ext4; use "xfs_growfs /mnt/app" for xfs</i><br/>
            </pre>
            <p>No <code>growpart</code> available? Use <code>parted /dev/sda resizepart 3 100%</code> instead,
                then continue with <code>pvresize</code>.</p>

            <h3>Inspecting</h3>
            <pre className={globalStyles.code}>
    <b>root@host:~#</b> pvs <i># list physical volumes</i><br/>
    <b>root@host:~#</b> vgs <i># list volume groups</i><br/>
    <b>root@host:~#</b> lvs <i># list logical volumes</i><br/>
            </pre>

            <p>See <b>Set disk quota</b> to limit how much of an LV a given user or group can consume.</p>
        </div>
    )
}
